import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User, CompanyUser, LoginCredentials, LoginWithOtpCredentials, RegisterData, ApiResponse, LoginResponse, RegisterResponse } from '../../types';
import * as authService from '../../services/authService';
import cookieService from '../../services/cookieService';

const token = cookieService.get('token');
const refreshToken = cookieService.get('refreshToken');

const initialState: AuthState = {
  user: null,
  currentCompanyUser: null,
  token,
  refreshToken,
  isAuthenticated: !!(token && refreshToken),
  isLoading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response.data;
    } catch (error: any) {
      // Pass through the complete error object for special handling (like email verification)
      const errorData = error.response?.data;
      return rejectWithValue({
        message: errorData?.error || 'Login failed',
        code: errorData?.code,
        data: errorData?.data
      });
    }
  }
);

export const loginWithOtp = createAsyncThunk(
  'auth/loginWithOtp',
  async (credentials: LoginWithOtpCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.loginWithOtp(credentials);
      return response.data;
    } catch (error: any) {
      const errorData = error.response?.data;
      return rejectWithValue({
        message: errorData?.error || 'Login failed',
        code: errorData?.code,
        data: errorData?.data
      });
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Registration failed');
    }
  }
);

export const registerCompany = createAsyncThunk(
  'auth/registerCompany',
  async (companyData: RegisterData & { companyName: string }, { rejectWithValue }) => {
    try {
      const response = await authService.registerCompany(companyData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Company registration failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      // Unsubscribe from push notifications before logging out
      try {
        const pushNotificationManager = (await import('../../utils/pushNotificationManager')).default;
        await pushNotificationManager.unsubscribe();
        console.log('✅ Unsubscribed from push notifications on logout');
      } catch (pushError) {
        console.warn('Failed to unsubscribe from push on logout:', pushError);
        // Don't fail logout if push unsubscribe fails
      }

      await authService.logout();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Logout failed');
    }
  }
);

export const refreshTokenAsync = createAsyncThunk(
  'auth/refreshToken',
  async (refreshToken: string, { rejectWithValue }) => {
    try {
      const response = await authService.refreshToken(refreshToken);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Token refresh failed');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser();
      // The response is already the ApiResponse structure: {success: true, data: user}
      return response.data; // This should be the user object
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to get user');
    }
  }
);

export const getPermissionContext = createAsyncThunk(
  'auth/getPermissionContext',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getPermissionContext();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to get permission context');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (userData: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Profile update failed');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (passwordData: { currentPassword: string; newPassword: string }, { rejectWithValue }) => {
    try {
      const response = await authService.changePassword(passwordData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Password change failed');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email, phone }: { email?: string; phone?: string }, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword({ email, phone });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Password reset request failed');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, password }: { token: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(token, password);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Password reset failed');
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await authService.verifyEmail(token);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Email verification failed');
    }
  }
);

export const resendVerificationEmail = createAsyncThunk(
  'auth/resendVerificationEmail',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.resendVerificationEmail();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to resend verification email');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string }>) => {
      const { user, token, refreshToken } = action.payload;
      state.user = user;
      state.token = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.error = null;
      // Set tokens in cookies with 7 day expiry for refreshToken, 30 hours for access token
      cookieService.setWithHours('token', token, 30);
      cookieService.set('refreshToken', refreshToken, { expires: 7 });
    },
    clearCredentials: (state) => {
      state.user = null;
      state.currentCompanyUser = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      cookieService.delete('token');
      cookieService.delete('refreshToken');
    },
    setCurrentCompanyUser: (state, action: PayloadAction<CompanyUser>) => {
      state.currentCompanyUser = action.payload;
    },
    updateCompanyUserPermissions: (state, action: PayloadAction<{ featurePermissions: import('../../constants/featurePermissions').FeaturePermission[] }>) => {
      if (state.currentCompanyUser) {
        state.currentCompanyUser.featurePermissions = action.payload.featurePermissions;
      }
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.error = null;
          // Set tokens in cookies with appropriate expiry
          cookieService.setWithHours('token', action.payload.token, 30);
          cookieService.set('refreshToken', action.payload.refreshToken, { expires: 7 });
          localStorage.setItem("rb_user_email", action.payload.user.email || "");
          localStorage.setItem("rb_user_name", action.payload.user.firstName ? `${action.payload.user.firstName} ${action.payload.user.lastName || ''}`.trim() : "");
          localStorage.setItem("rb_user_phone", action.payload.user.phone || "");
          // @ts-ignore
          window?.ChatbotBuilder?.setUserData({
            email: action.payload.user.email || '',
            name: action.payload.user.firstName ? `${action.payload.user.firstName} ${action.payload.user.lastName || ''}`.trim() : '',
            phone: action.payload.user.phone || ''
          });
          // @ts-ignore
          window?.ChatbotBuilder?.resetChat();
          // Clean up any orphaned push subscriptions from previous user, then re-subscribe if enabled
          const userPayload = action.payload.user;
          (async () => {
            try {
              const pushNotificationManager = (await import('../../utils/pushNotificationManager')).default;

              // First, clean up any orphaned subscriptions
              await pushNotificationManager.cleanupOrphanedSubscriptions();

              const pushEnabled = userPayload?.notificationPreferences?.push;
              if (pushEnabled) {
                await pushNotificationManager.init();
                const isSubscribed = await pushNotificationManager.isSubscribed();

                if (!isSubscribed) {
                  console.log('🔔 Re-subscribing to push notifications after session restore...');
                  const success = await pushNotificationManager.subscribe();
                  if (success) {
                    console.log('✅ Re-subscribed to push notifications');
                  }
                }
              }
            } catch (error) {
              console.warn('Failed to handle push subscriptions on login:', error);
            }
          })();
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Login failed';
        state.isAuthenticated = false;
      })
      .addCase(loginWithOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.error = null;
          cookieService.setWithHours('token', action.payload.token, 30);
          cookieService.set('refreshToken', action.payload.refreshToken, { expires: 7 });
          localStorage.setItem("rb_user_email", action.payload.user.email || "");
          localStorage.setItem("rb_user_name", action.payload.user.firstName ? `${action.payload.user.firstName} ${action.payload.user.lastName || ''}`.trim() : "");
          localStorage.setItem("rb_user_phone", action.payload.user.phone || "");
          // @ts-ignore
          window?.ChatbotBuilder?.setUserData({
            email: action.payload.user.email || '',
            name: action.payload.user.firstName ? `${action.payload.user.firstName} ${action.payload.user.lastName || ''}`.trim() : '',
            phone: action.payload.user.phone || ''
          });
          // @ts-ignore
          window?.ChatbotBuilder?.resetChat();
        }
      })
      .addCase(loginWithOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string'
          ? action.payload
          : (action.payload as { message?: string } | undefined)?.message || 'Login failed';
        state.isAuthenticated = false;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, _action) => {
        state.isLoading = false;
        // With strict verification, registration doesn't log user in
        // User remains unauthenticated until email verification is complete
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Registration failed';
        state.isAuthenticated = false;
      })
      // Register Company
      .addCase(registerCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerCompany.fulfilled, (state, _action) => {
        state.isLoading = false;
        // Company registration requires email verification
        state.error = null;
      })
      .addCase(registerCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Company registration failed';
        state.isAuthenticated = false;
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.currentCompanyUser = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = null;
        cookieService.delete('token');
        cookieService.delete('refreshToken');
        localStorage.removeItem("rb_user_email");
        localStorage.removeItem("rb_user_name");
        localStorage.removeItem("rb_user_phone");
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Logout failed';
      })
      // Refresh Token
      .addCase(refreshTokenAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.error = null;
          // Set tokens in cookies with appropriate expiry
          cookieService.setWithHours('token', action.payload.token, 30);
          cookieService.set('refreshToken', action.payload.refreshToken, { expires: 7 });
        }
      })
      .addCase(refreshTokenAsync.rejected, (state, action) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = (action.payload as string) || 'Token refresh failed';
        cookieService.delete('token');
        cookieService.delete('refreshToken');
      })
      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;
          state.error = null;

          // Re-subscribe to push notifications if enabled (for session restore)
          (async () => {
            try {
              const pushNotificationManager = (await import('../../utils/pushNotificationManager')).default;

              // Check if user has push notifications enabled and is not already subscribed
              const pushEnabled = action.payload?.notificationPreferences?.push;
              if (pushEnabled) {
                await pushNotificationManager.init();
                const isSubscribed = await pushNotificationManager.isSubscribed();

                if (!isSubscribed) {
                  console.log('🔔 Re-subscribing to push notifications after session restore...');
                  const success = await pushNotificationManager.subscribe();
                  if (success) {
                    console.log('✅ Re-subscribed to push notifications');
                  }
                }
              }
            } catch (error) {
              console.warn('Failed to handle push subscriptions on session restore:', error);
            }
          })();
        }
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to get user';
        state.isAuthenticated = false;
        state.user = null;
        state.currentCompanyUser = null;
        state.token = null;
        state.refreshToken = null;
        cookieService.delete('token');
        cookieService.delete('refreshToken');
      })
      // Get Permission Context
      .addCase(getPermissionContext.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPermissionContext.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          if (state.user && action.payload.user) {
            state.user = {
              ...state.user,
              subRole: action.payload.user.subRole
            };
          }
          state.currentCompanyUser = action.payload.companyUser;
          state.isAuthenticated = true;
          state.error = null;
        }
      })
      .addCase(getPermissionContext.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to get permission context';
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload;
          state.error = null;
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.error = (action.payload as string) || 'Profile update failed';
      })
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.error = null;
          // Set tokens in cookies with appropriate expiry
          cookieService.setWithHours('token', action.payload.token, 30);
          cookieService.set('refreshToken', action.payload.refreshToken, { expires: 7 });
        }
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Password change failed';
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Password reset request failed';
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.error = null;
          // Set tokens in cookies with appropriate expiry
          cookieService.setWithHours('token', action.payload.token, 30);
          cookieService.set('refreshToken', action.payload.refreshToken, { expires: 7 });
        }
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Password reset failed';
      })
      // Verify Email
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.isLoading = false;
        if (state.user) {
          state.user.isEmailVerified = true;
        }
        state.error = null;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Email verification failed';
      })
      // Resend Verification Email
      .addCase(resendVerificationEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendVerificationEmail.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resendVerificationEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to resend verification email';
      });
  },
});

export const { clearError, setCredentials, clearCredentials, updateUser, setCurrentCompanyUser, updateCompanyUserPermissions } = authSlice.actions;

export default authSlice.reducer;
